// src/components/Tags/AddTagDialog.js
// @flow

import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';
import { TwitterPicker } from 'react-color';
import Downshift from 'downshift';
import PhoneInput, { formatPhoneNumberIntl } from 'react-phone-number-input';
import Popper from '@mui/material/Popper';
import InputAdornment from '@mui/material/InputAdornment';
import Button from '@mui/material/Button';
import Input from '@mui/material/Input';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';

import TagReadonly from '../SharedComponents/TagReadonly';
import SPBackend from '../../services/SPBackend';
import AutocompleteProperty from '../Properties/AutocompleteProperty';

import { selectUser } from '../../reduxStore/modules/user';

import { useI18n } from '../../contexts/I18nContext';
import { log } from '../../utils/jsUtils';
import addTagDialogStyles from './Styles/AddTagDialog.Style';
import { useTheme } from '@mui/material/styles';

// If you need a type for unit
// import type { Unit } from '../../services/SPBackend';

type Props = {
  open: boolean,
  setOpen: (val: boolean) => void,
  triggerAction: (tagData: any, cbSuccess: Function, cbFailure: Function) => void
  // any other props
};

const AddTagDialog = (props: Props) => {
  const { open, setOpen, triggerAction } = props;

  // Hooks
  const theme = useTheme();
  const dispatch = useDispatch();
  const i18n = useI18n();

  // If needed, get user from Redux:
  const user = useSelector(selectUser);

  // Local State
  const [anchorEl, setAnchorEl] = useState(null);
  const [displayColorPicker] = useState(false); // or remove if not used
  const [loading, setLoading] = useState(false);
  const [newTag, setNewTag] = useState({
    Color: '#CCCCCC',
    Name: ''
  });

  // Helpers
  const emptyForm = () => {
    setNewTag({
      ...newTag,
      Color: '#CCCCCC',
      Name: ''
    });
  };

  const handleColorPicked = (color, event) => {
    log('[handleColorPicked] color: ', color);
    setNewTag((prev) => ({
      ...prev,
      Color: color.hex
    }));
    setAnchorEl(null);
  };

  const handleColorPickerClick = (tag, event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const openColorPicker = Boolean(anchorEl);
  const colorPickerId = openColorPicker ? 'simple-popper' : '';

  // Rendering
  // log('[AddTagDialog] newTag: ', newTag);

  return (
    <Dialog
      aria-labelledby="form-add-tag"
      disableEnforceFocus
      onClose={() => {
        setOpen(false);
        emptyForm();
      }}
      open={open}
    >
      <form
        autoComplete="off"
        noValidate
        // If you had used classNames(...classes), switch to inline or `sx`:
        // We can apply multiple style objects with array or object spread:
        // Example: sx={[ addTagDialogStyles.dialogFormCtr(theme), addTagDialogStyles.flexContainer(theme) ]}
        style={addTagDialogStyles.dialogFormCtr(theme)}
        onSubmit={(e) => {
          e.preventDefault();
          setLoading(true);
          triggerAction(
            newTag,
            () => {
              setLoading(false);
              setOpen(false);
              emptyForm();
            },
            () => {
              setLoading(false);
            }
          );
        }}
      >
        <DialogTitle id="form-dialog-title">
          {`Add a new ${i18n.t('tag')}`}
        </DialogTitle>

        <DialogContent
          sx={{
            ...addTagDialogStyles.grow(theme),
            ...addTagDialogStyles.flexContainer(theme)
          }}
        >
          {newTag && newTag.Name !== '' && (
            <div>
              {`Your new tag will look like this:`}
              <br />
              <TagReadonly readonly renderTags={[newTag]} />
            </div>
          )}

          <div>
            <TextField
              sx={addTagDialogStyles.margin(theme)}
              id="input-with-icon-textfield"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <div>
                      <div
                        style={addTagDialogStyles.colorPickerRainbow(theme)}
                        onClick={(e) => handleColorPickerClick(newTag, e)}
                      />
                    </div>
                  </InputAdornment>
                )
              }}
              label="Tag Name"
              onChange={(e) =>
                setNewTag({
                  ...newTag,
                  Name: e.target.value
                })
              }
              value={newTag.Name}
            />

            <Popper
              anchorEl={anchorEl}
              sx={addTagDialogStyles.popperCtr}
              id={colorPickerId}
              open={openColorPicker}
              placement="bottom"
            >
              <TwitterPicker onChangeComplete={handleColorPicked} />
            </Popper>
          </div>
        </DialogContent>

        <DialogActions>
          <Button
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setOpen(false);
              emptyForm();
            }}
          >
            {i18n.t('Cancel')}
          </Button>
          <Button
            color="primary"
            disabled={loading}
            type="submit"
            variant="contained"
          >
            {`Add ${i18n.t('tag')}`}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddTagDialog;